import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { DepositButton } from '@/feature/account-details/components/DepositButton';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useEntryRules } from '@/feature/betslip-pickem/hooks/use-entry-rules';
import { GuestLoginBanner } from '@/feature/guest-user/components/GuestBanner';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useParentGestureLock } from '@/hooks/use-parent-gesture-lock';
import { useUserStore } from '@/hooks/use-user-store';
import { GameMode, GameType } from '@/types/api.generated';
import { isWeb } from '@/utils/constants-platform-specific';
import { triggerHapticFeedback } from '@/utils/haptic-feedback';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { useBetslipData } from '../hooks/use-betslip-data';
import { useBetslipStore } from '../hooks/use-betslip-store';

export const Footer = ({
    hasValidationError,
    hasBalanceError,
    errorMessage,
    placeEntry,
    isLoading,
    goToSubmitEntry,
}: {
    hasValidationError?: boolean;
    hasBalanceError?: boolean;
    errorMessage?: string;
    placeEntry: (amount: number) => Promise<void>;
    isLoading: boolean;
    goToSubmitEntry: () => void;
}) => {
    const { t } = useTranslation(['betslip_pickem', 'common']);
    const validatingBetslip = useBetslipStore(state => state.validating);
    const isSyncingPicks = useBetslipStore(state => state.isSyncingPicks);
    const { validPicks, isGameModeValid } = useBetslipData();
    const { gameTypes } = useEntryRules();
    const activeGameMode = useBetslipStore(state => state.activeGameMode);
    const isEntryValid = isGameModeValid(activeGameMode);
    const selectedPromo = useBetslipStore(state => state.selectedPromo);
    const isMysteryMode = selectedPromo?.promotion.type === GameMode.Mystery;

    const entryAmount = useBetslipStore(state => state.entryAmount);

    const p2pGameType = gameTypes.includes(GameType.P2p);
    const amount = Number(entryAmount);

    const isSubmitting = validatingBetslip || isSyncingPicks || isLoading;
    const buttonDisabled = !isEntryValid || !validPicks || !!hasValidationError || !!hasBalanceError;

    useParentGestureLock(isLoading);

    const submitEntry = useCallback(() => {
        triggerHapticFeedback();
        if (p2pGameType) {
            BetrAnalytics.trackEvent(AnalyticsEvent.PLACE_ENTRY_P2P, { amount: entryAmount });
        } else {
            BetrAnalytics.trackEvent(AnalyticsEvent.PLACE_ENTRY, { amount: entryAmount });
        }
        if (isMysteryMode) {
            // Mystery mode submits from SubmitEntry so the wheel can spin in parallel.
            goToSubmitEntry();
            return;
        }
        placeEntry(amount);
    }, [amount, entryAmount, goToSubmitEntry, isMysteryMode, p2pGameType, placeEntry]);

    const guest = useUserStore(state => state.guest);

    const insets = useSafeAreaInsets();

    return (
        <Box
            width={'100%'}
            backgroundColor={ds.colorToken.background.neutral.subtler}
            borderStyle="solid"
            borderColor={ds.colorToken.border.strong}
            borderTopWidth={1}
            alignItems={isWeb ? 'center' : undefined}
        >
            <MaxWidthWrapper style={{ paddingBottom: insets.bottom + 16 }} padding={'s16'} gap={'s16'}>
                {hasValidationError || hasBalanceError ? (
                    <Box alignItems={'center'}>
                        <Text variant="labelLarge" color={ds.colorToken.text.danger} textAlign={'center'}>
                            {hasValidationError ? errorMessage : t('entry_amount_exceeds_balance')}
                        </Text>
                    </Box>
                ) : null}
                {guest ? (
                    <GuestLoginBanner />
                ) : (
                    <Box>
                        {hasBalanceError ? (
                            <DepositButton buttonHierarchy="secondary" customLabel={t('deposit_to_place_entry')} />
                        ) : (
                            <Button
                                hierarchy={'primaryLilac'}
                                loading={isSubmitting}
                                loaderColors={submitLoaderColors}
                                label={
                                    !isEntryValid || !validPicks
                                        ? t('place_entry')
                                        : t('place_entry_amount', { amount: toLocaleCurrency(entryAmount) })
                                }
                                disabled={buttonDisabled}
                                onPress={submitEntry}
                                testID="placeEntryBtn"
                            />
                        )}
                    </Box>
                )}
            </MaxWidthWrapper>
        </Box>
    );
};

const submitLoaderColors = { main: ds.colorValue.icon.primary, second: 'transparent' };
